/**
 * CampoCard — linha arrastavel de um campo no canvas do Editor.
 *
 * Usa @dnd-kit/sortable (mesma mecanica do ConfigurarPipelinePanel). Mostra
 * label + tipo + indicadores (obrigatorio, mapeado, condicional) e abre a
 * edicao ao clicar no corpo. O handle (icone de mover) e a alca de arraste.
 * `somenteLeitura` (so formulario:view) tira arraste e exclusao, e o clique
 * abre o campo so para ver (FORM-41).
 */
import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  FiEdit3,
  FiEye,
  FiGitBranch,
  FiLink,
  FiMove,
  FiTrash2,
} from "react-icons/fi";

import { TIPO_CAMPO_LABEL, tipoSemInput } from "../../services/formularios";

import { CampoActions, CampoInfo, CampoRow, DragHandle } from "./styles";

function temCondicaoAtiva(campo) {
  return Boolean(
    campo?.condicao &&
      Array.isArray(campo.condicao.regras) &&
      campo.condicao.regras.length > 0
  );
}

function CampoCard({ campo, onEditar, onExcluir, somenteLeitura = false }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: String(campo._key), disabled: somenteLeitura });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { opacity: 0.55, zIndex: 2 } : null),
  };

  const ehSecao = tipoSemInput(campo.tipo);
  const nome = campo.label || (ehSecao ? "Seção sem título" : "Campo sem rotulo");
  const verbo = somenteLeitura ? "Ver" : "Editar";

  return (
    <CampoRow ref={setNodeRef} style={style} $secao={ehSecao}>
      {somenteLeitura ? (
        <span aria-hidden="true" />
      ) : (
        <DragHandle
          type="button"
          aria-label={`Reordenar campo ${nome}`}
          {...attributes}
          {...listeners}
        >
          <FiMove aria-hidden="true" />
        </DragHandle>
      )}

      <CampoInfo type="button" onClick={onEditar} title={`${verbo} campo`}>
        <span className="nm">{nome}</span>
        <span className="meta">
          <span>{TIPO_CAMPO_LABEL[campo.tipo] || campo.tipo}</span>
          {campo.obrigatorio && !ehSecao && (
            <span className="badge danger no-dot" style={{ fontSize: 9 }}>
              obrigatorio
            </span>
          )}
          {campo.mapeia_para && (
            <span
              title={`Mapeia para ${campo.mapeia_para}`}
              style={{ display: "inline-flex", alignItems: "center", gap: 2 }}
            >
              <FiLink size={10} aria-hidden="true" /> lead
            </span>
          )}
          {temCondicaoAtiva(campo) && (
            <span
              title="Tem logica condicional"
              style={{ display: "inline-flex", alignItems: "center", gap: 2 }}
            >
              <FiGitBranch size={10} aria-hidden="true" /> condicional
            </span>
          )}
        </span>
      </CampoInfo>

      <CampoActions>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={onEditar}
          aria-label={`${verbo} campo ${nome}`}
        >
          {somenteLeitura ? (
            <FiEye size={13} aria-hidden="true" />
          ) : (
            <FiEdit3 size={13} aria-hidden="true" />
          )}
        </button>
        {!somenteLeitura && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={onExcluir}
            aria-label={`Excluir campo ${nome}`}
          >
            <FiTrash2 size={13} aria-hidden="true" />
          </button>
        )}
      </CampoActions>
    </CampoRow>
  );
}

export default CampoCard;
